<template>
    <div class="form-group">
        <label for="" v-if="removeTitle === false">
            <strong>
                Estimated Route
            </strong>
        </label>
        <div class="position-relative map-image-container" v-show="mapImage">
            <img :src="internalImageRoute"
                 class="w-100"
                 alt="Image of drivers route between collection and delivery locations"/>

            <div class="position-absolute top-0 bottom-0 end-0 start-0 map-button-container">
                <button @click="clickShowInteractiveMap" class="btn btn-primary">Show Route</button>
            </div>
        </div>

        <div class="map-container" v-show="!mapImage">
            <div ref="mapElement" class="map-element"></div>
        </div>
    </div>
</template>

<style scoped>

.map-button-container {
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.25s, visibility 0.25s;
}

.map-image-container:hover .map-button-container {
    opacity: 1;
    visibility: visible;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.5);
}

.map-container {
    width: 100%;
    overflow: hidden;
}

.map-element {
    width: 100%;
    max-width: 100%;
    height: 400px;
}

</style>

<script setup>

import { ref, onMounted } from 'vue';
import { Loader } from "@googlemaps/js-api-loader";
import axios from "axios";

const props = defineProps({
    imageRoute: {
        type: String,
        required: false,
        default: '/images/movements/awaiting-route-placeholder.png'
    },
    points: {
        type: Array,
        required: true
    },
    removeTitle: {
        type: Boolean,
        required: false,
        default: false
    }
});

const internalImageRoute = ref(props.imageRoute);
const map = ref(null);
const mapElement = ref(null);
const loader = new Loader({
    apiKey: 'AIzaSyD_tfgEUWxTtVczG5euJnkbeZ9DryHp4F4',
    version: "weekly",
    libraries: ["places", "geocoding", "maps", "marker"],
});

const mapImage = ref(true);

async function loadLibraries() {
    const [
        {Map},
        {Geocoder},
        {LatLng},
        {DirectionsService},
        {DirectionsStatus},
        {TravelMode},
        {UnitSystem},
        {InfoWindow},
        {DirectionsRenderer}
    ] = await Promise.all([
        google.maps.importLibrary("maps"),
        google.maps.importLibrary("geocoding"),
        google.maps.importLibrary("core"),
        google.maps.importLibrary("routes"),
        google.maps.importLibrary("routes"),
        google.maps.importLibrary("routes"),
        google.maps.importLibrary("core"),
        google.maps.importLibrary("maps"),
        google.maps.importLibrary("routes")
    ]);
    return {
        Map,
        Geocoder,
        LatLng,
        DirectionsService,
        DirectionsStatus,
        TravelMode,
        UnitSystem,
        InfoWindow,
        DirectionsRenderer
    };
}

function buildMarkerIcon(markerColour) {
    const colour = String(markerColour ?? 'red').toLowerCase();
    // Google marker icon set uses orange-dot (not yellow-dot).
    const normalisedColour = colour === 'yellow' ? 'orange' : colour;
    return `https://maps.google.com/mapfiles/ms/icons/${normalisedColour}-dot.png`;
}

async function clickShowInteractiveMap() {

    await loader.importLibrary('places');

    let {
        Map,
        DirectionsService,
        TravelMode,
        UnitSystem,
        InfoWindow,
        DirectionsRenderer,
        LatLng
    } = await loadLibraries();

    mapImage.value = false;


    map.value = new Map(mapElement.value, {
        mapId: "default",
        center: {
            lat: 55.1198611,
            lng: -4.2826482
        },
        zoom: 14,
        fullscreenControl: false,
        scrollwheel: false,
    });

    let waypoints = [];

    props.points.forEach((point) => {

        const coordinate = new LatLng(point.lat, point.lng);

        const infoWindow = new InfoWindow({
            content: point.infoWindowContent,
        });

        const markerOptions = {
            position: coordinate,
            map: map.value,
            icon: {
                url: buildMarkerIcon(point.markerColour),
            },
            title: point.title ?? point.infoWindowHeaderContent ?? '',
        };



        const marker = new google.maps.Marker(markerOptions);
        marker.addListener('click', () => {
            infoWindow.open({
                map: map.value,
                anchor: marker,
            });
        });
        waypoints.push(coordinate);
    });

    const directionService = new DirectionsService();

    const directionsRenderer = new DirectionsRenderer({
        map: map.value,
        polylineOptions: {
            strokeColor: '#1ab394'
        },
        suppressMarkers: true,
    });

    const result = await directionService.route({
        origin: waypoints[0],
        destination: waypoints[waypoints.length - 1],
        travelMode: TravelMode.DRIVING,
        unitSystem: UnitSystem.IMPERIAL,
    });
    directionsRenderer.setDirections(result);


    if (waypoints.length > 2) {
        let mmDirectionsRenderer = new DirectionsRenderer({
            map: map.value,
            polylineOptions: {
                strokeColor: '#1c84c6',
                zIndex: 9999
            },
            preserveViewport: true,
            suppressMarkers: true,
        });

        let locations = [];

        waypoints.forEach(function (waypoint) {
            locations.push({location: waypoint});
        });

        directionService.route({
            origin: waypoints[0],
            waypoints: locations,
            destination: waypoints[waypoints.length - 1],
            travelMode: TravelMode.DRIVING,
            unitSystem: UnitSystem.IMPERIAL
        }, function (result, status) {
            if (status === google.maps.DirectionsStatus.OK) {
                mmDirectionsRenderer.setDirections(result);
            } else {
                console.error(status);
            }
        });
    }
}

async function getInternalImageRoute() {
    try {
        const response = await axios.post(route('api.movements.helpers.get-static-map'), {
            points: props.points,
            width: 550,
            height: 450,
        });
        internalImageRoute.value = response.data.url;
    } catch (error) {
        console.error('Error fetching map image:', error);
        internalImageRoute.value = props.imageRoute;
    }
}

onMounted(() => {
    getInternalImageRoute();
});
</script>
